import { Input, Form, Tag, Tooltip } from "antd";
import type { FormInstance } from "antd";

import { restrictInputToNumbers } from "utils/inpututils";
import { useMemo } from "react";
import { getDaysText } from "utils/utils";
import { DateUtils } from "utils/dateutils";
import { CheckCircleOutlined } from "@ant-design/icons";
import { IMaterial } from "./type-definitions";

function MaterialDurationsSection({
  material,
  form,
  onValidityChange
}: {
  material: IMaterial;
  form: FormInstance;
  onValidityChange?: (hasErrors: boolean) => void;
}) {
  const formValues = Form.useWatch([], form);

  const initialValues = useMemo(() => {
    return material.offsets?.reduce((acc: any, offset) => {
      acc[offset.id] = offset.value;
      return acc;
    }, {});
  }, [material.offsets]);

  const durationsTotal = useMemo(() => {
    return Object.values(formValues || {}).reduce<number>(
      (acc: number, value: any) => acc + Number(value),
      0
    );
  }, [formValues]);

  if (!material.offsets) return null;

  return (
    <div data-testid="material-durations-section">
      <div
        className="text-sm font-semibold text-color-1 mb-5"
        data-testid="material-durations-header"
      >
        Enter Lead Time
      </div>
      <div className="w-full lg:max-w-[360px]">
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onFieldsChange={(_, allFields) => {
            onValidityChange?.(
              // antd's onFieldsChange always populates `errors` as an array (the
              // FieldData type marks it optional, hence the guard), so the `?? 0`
              // fallback arm is unreachable defensive code.
              /* v8 ignore next */
              allFields.some((field) => (field.errors?.length ?? 0) > 0)
            );
          }}
        >
          {material?.offsets
            ?.sort((a, b) => a.order - b.order)
            .map((offset) => (
              <Tooltip
                key={offset.order}
                placement="right"
                title={
                  !offset.is_editable
                    ? "Lead Time cannot be edited for completed steps. "
                    : ""
                }
              >
                <Form.Item
                  label={
                    !offset.is_editable ? (
                      <div className="flex justify-between items-end w-full">
                        <div>{offset.name}</div>
                        {!offset.is_editable && (
                          <div className="-mr-2 py-[2px]">
                            <Tag
                              className="rounded-[4px] text-ciq-success-text font-normal py-[2px]"
                              icon={
                                <CheckCircleOutlined
                                  style={{ color: "var(--ciq-success)" }}
                                />
                              }
                              color="green"
                            >
                              Completed
                            </Tag>
                          </div>
                        )}
                      </div>
                    ) : (
                      offset.name
                    )
                  }
                  name={offset.id}
                  rules={[
                    {
                      required: offset.is_editable,
                      message: "Enter a valid number of days"
                    }
                  ]}
                  key={offset.id}
                  data-testid={`material-duration-item-${offset.id}`}
                >
                  <Input
                    type="number"
                    suffix="Days"
                    placeholder="0"
                    min={0}
                    onKeyDown={restrictInputToNumbers}
                    tabIndex={0}
                    data-testid={`material-duration-input-${offset.id}`}
                    disabled={!offset.is_editable}
                  />
                </Form.Item>
              </Tooltip>
            ))}
          <div
            className="mt-4 flex items-center justify-between text-sm font-semibold "
            data-testid="material-durations-total-row"
          >
            <div
              className="text-color-1"
              data-testid="material-durations-total-label"
            >
              Total
            </div>
            <div
              className="text-color-3"
              data-testid="material-durations-total-value"
            >
              {durationsTotal} {getDaysText(durationsTotal)}
            </div>
          </div>
          {material.updated_at ? (
            <div
              className="mt-2.5 text-xs text-color-1 text-center italic"
              data-testid="material-durations-last-submitted"
            >
              Last submitted on:{" "}
              {DateUtils.format(material.updated_at, "MM-DD-YYYY")} at{" "}
              {DateUtils.format(material.updated_at, "hh:mm A (z)")}
            </div>
          ) : null}
        </Form>
      </div>
    </div>
  );
}

export default MaterialDurationsSection;
